import { useId } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AlertTriangle, ChevronDown, LogOut, UserCircle } from 'lucide-react';
import { Breadcrumbs } from '../Breadcrumbs';
import { useDismissibleDetails } from '../../hooks/useDismissibleDetails';
import { cn } from '../../lib/utils';
import { menuItemClassName, menuSeparatorClassName, popoverSurfaceClassName } from '../ui/surface';
import { Avatar, getAvatarInitials } from '../ui/avatar';

export function PortalHeader() {
    const { user, logout } = useAuth0();
    const accountMenuRef = useDismissibleDetails();
    const accountSummaryId = useId();

    return (
        <>
            <header className="sticky top-0 z-40 h-[var(--app-bar-height)] border-b border-neutral-200/70 admin-bar-material">
                <div className="mx-auto flex h-full w-full max-w-screen-2xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
                    <Link to="/admin" className="flex min-w-0 items-center">
                        <div className="flex h-7 items-center gap-2.5">
                            <img
                                src="/Horizontal-FullColor-1.svg"
                                alt="WEVO"
                                className="-translate-y-0.5 h-6 w-auto shrink-0 object-contain object-left"
                                decoding="async"
                            />
                            <span className="h-5 w-px shrink-0 bg-neutral-200" aria-hidden="true" />
                            <span className="min-w-0 truncate text-base font-semibold leading-none tracking-wide text-neutral-500">
                                Admin Portal
                            </span>
                        </div>
                    </Link>

                    <details ref={accountMenuRef} className="relative z-50 shrink-0">
                        <summary
                            id={accountSummaryId}
                            aria-label="Account menu"
                            className="flex cursor-pointer list-none items-center gap-2 rounded-full bg-transparent px-1 py-1 text-sm transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 [&::-webkit-details-marker]:hidden"
                        >
                            <Avatar
                                src={user?.picture}
                                initials={getAvatarInitials(user?.name, user?.email)}
                                className="size-8 text-xs"
                            />
                            <div className="hidden text-left md:block">
                                <p className="max-w-44 truncate text-sm font-medium text-neutral-900">
                                    {user?.name || user?.email || 'User'}
                                </p>
                                <p className="max-w-44 truncate text-xs text-neutral-500">
                                    {user?.email}
                                </p>
                            </div>
                            <ChevronDown className="size-4 text-neutral-500" aria-hidden />
                        </summary>
                        <div
                            className={cn(
                                popoverSurfaceClassName,
                                'absolute right-0 z-50 mt-2 w-64 p-2',
                            )}
                        >
                            <div className="px-3 py-2">
                                <p className="truncate text-sm font-medium text-neutral-900">
                                    {user?.name || user?.email || 'User'}
                                </p>
                                <p className="truncate text-xs text-neutral-500">{user?.email}</p>
                            </div>
                            <div className={menuSeparatorClassName} aria-hidden />
                            <div
                                role="menu"
                                aria-labelledby={accountSummaryId}
                                className="flex flex-col"
                            >
                                <Link to="/profile" role="menuitem" className={menuItemClassName}>
                                    <UserCircle className="size-4" aria-hidden />
                                    Profile
                                </Link>
                                <Link
                                    to="/admin/attention"
                                    role="menuitem"
                                    className={menuItemClassName}
                                >
                                    <AlertTriangle className="size-4" aria-hidden />
                                    Attention center
                                </Link>
                                <div role="separator" className={menuSeparatorClassName} />
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() =>
                                        logout({
                                            logoutParams: { returnTo: window.location.origin },
                                        })
                                    }
                                    className={cn(menuItemClassName, 'w-full')}
                                >
                                    <LogOut className="size-4" aria-hidden />
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </details>
                </div>
            </header>
            <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
                <Breadcrumbs />
            </div>
        </>
    );
}
